<template>
  <div class="v_shopTheLook_container cc" :class="{ 'is-landscape': config.isLandscape === true }" @click="onContainerClick">
    <a
      v-if="showMasterLink && config.masterUrl"
      :href="config.masterUrl"
      class="masterLink"
      :aria-label="`STL: ${config.ariaLabel}`"
      click-count
    />

    <button
      v-if="showMagnifier"
      type="button"
      class="magnifier"
      :aria-label="`Look vergroessern: ${config.ariaLabel || ''}`"
      @click.stop="emitOpenFullscreen"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-zoom-in-icon lucide-zoom-in"><circle cx="11" cy="11" r="8" /><line x1="21" x2="16.65" y1="21" y2="16.65" /><line x1="11" x2="11" y1="8" y2="14" /><line x1="8" x2="14" y1="11" y2="11" /></svg>
    </button>

    <picture>
      <source v-if="config.imageUrlMobile" media="(max-width: 576px)" :srcset="config.imageUrlMobile">
      <img :src="config.imageUrl" class="shopTheLook_Image" :aria-label="config.ariaLabel" :alt="config.ariaLabel">
    </picture>

    <div class="v_shopTheLook_tags">
      <span
        v-for="entry in visibleDots"
        :key="entry.dot.id"
        :data-idx="entry.index"
        class="v_shopTheLook_tag"
        :class="{ active: entry.dot.isOpen, notfound: !entry.dot.exists }"
        :style="tagStyle(entry.dot)"
        @click.stop="onTagClick(entry.index, $event)">

        <span class="dot-marker" />

        <!-- info box -->
        <div v-if="entry.dot.isOpen && entry.dot.data" class="itemInfo" :class="entry.dot.orientation"
             :aria-label="`STL: ${ dotName(entry.dot)}`"
             @click.stop>

          <div class="contents stlInfo">
            <!-- Hersteller -->
            <span class="manufacturer">{{ manufacturerName(entry.dot) }}</span>

            <!-- Name -->
            <span class="name">{{ dotName(entry.dot) }}</span>

            <!-- Preis -->
            <span v-if="showPrice(entry.dot)" class="itemPrice">
              <span class="prices">
                <del
                  v-if="hasDiscount(entry.dot)"
                  class="crossprice"
                >{{ formatPrice(entry.dot.data.rrp) }}</del>
                <span :class="{ redPrice: hasDiscount(entry.dot) }">{{ formatPrice(entry.dot.data.price) }}</span>
              </span>
            </span>

            <div class="basket" @click.stop>
              <client-only v-if="entry.dot.data && entry.dot.data.id">
                <add-to-basket
                  :variation-id="entry.dot.variationId"
                  :is-salable="entry.dot.data.isSalable"
                  one-click
                  use-large-scale
                  :show-quantity="false"
                  :minimum-quantity="1"
                />
              </client-only>
            </div>
          </div>
          <a :href="productUrl(entry.dot)" class="followArrow" @click.stop>
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="css-i6dzq1"
            ><polyline points="9 18 15 12 9 6" /></svg>
          </a>
        </div>
      </span>
    </div>
  </div>
</template>

<script>
function buildDotStates (config) {
  return (config.dots || []).map(dot => ({
    ...dot,
    isOpen: !!dot.defaultOpen,
    exists: !!dot.variation,
    hideDot: false,
    data: dot.variation || null
  }))
}

export default {
  name: 'ShopTheLookItem',
  props: {
    config: {
      type: Object,
      required: true
    },
    showMagnifier: {
      type: Boolean,
      default: true
    },
    showMasterLink: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      dotStates: buildDotStates(this.config)
    }
  },
  computed: {
    visibleDots () {
      return this.dotStates
        .map((dot, index) => ({ dot, index }))
        .filter(entry => !entry.dot.hideDot)
    }
  },
  watch: {
    config: {
      deep: true,
      handler (newConfig) {
        this.dotStates = buildDotStates(newConfig)

        this.$nextTick(() => {
          this.applyVisibility()
        })
      }
    }
  },
  mounted () {
    this.applyVisibility()
    window.addEventListener('resize', this.applyVisibility)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.applyVisibility)
  },
  methods: {
    emitOpenFullscreen () {
      this.$emit('open-fullscreen')
    },
    syncDotState (mapper) {
      this.dotStates = this.dotStates.map((dot, index) => mapper(dot, index))
    },
    tagStyle (dot) {
      const s = {
        top: `${dot.posY}%`,
        left: `${dot.posX}%`,
        '--stl-posx': `${dot.posX}%`,
        '--stl-posy': `${dot.posY}%`
      }
      if (dot.posXmobile > 0) s['--stl-posx-mobile'] = `${dot.posXmobile}%`
      if (dot.posYmobile > 0) s['--stl-posy-mobile'] = `${dot.posYmobile}%`
      return s
    },
    applyVisibility () {
      const isMobile = window.innerWidth <= 576
      this.syncDotState(dot => ({
        ...dot,
        hideDot: isMobile ? !!dot.hiddenMobile : !!dot.hiddenDesktop
      }))
    },
    setOpenDot (idx) {
      this.syncDotState((dot, dotIndex) => ({
        ...dot,
        isOpen: dotIndex === idx ? !dot.isOpen : false
      }))
    },
    onTagClick (idx, event) {
      if (this.isDotContentClick(event)) {
        return
      }

      this.setOpenDot(idx)
    },
    onContainerClick (event) {
      if (this.isDotContentClick(event)) {
        return
      }

      this.setOpenDot()
    },
    isDotContentClick (event) {
      if (!event || !event.target || typeof event.target.closest !== 'function') {
        return false
      }

      return !!event.target.closest('.itemInfo, .basket, .followArrow')
    },
    dotName (dot) {
      return dot.data?.item?.names?.name1 || `Variation #${dot.variationId}`
    },
    manufacturerName (dot) {
      return dot.data?.item?.manufacturer?.name || ''
    },
    productUrl (dot) {
      if (dot && dot.data && dot.data.item) {
        const url = dot.data.item.names?.url ?? ''
        const itemId = dot.data.item.itemId ?? 0
        const variationId = dot.variationId ?? 0
        return `/${url}_${itemId}_${variationId}/`
      }

      return this.config.masterUrl || '#'
    },
    showPrice (dot) {
      return this.config.showPrice !== false && dot.data && dot.data.price !== null
    },
    hasDiscount (dot) {
      if (!dot.data) {
        return false
      }

      return Number(dot.data.rrp || 0) > Number(dot.data.price || 0)
    },
    formatPrice (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }

      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(Number(value))
    }
  }
}
</script>
